import { FIELD, PHYSICS, goalMouth, type Ball, type Player } from "@golazo/shared";
import { len2 } from "../core/math.js";
import { collideBallWithWalls } from "../physics/collisions.js";
import { stepBall } from "../physics/step.js";
import { kickLaunch, type KickContext } from "./kick.js";

/**
 * A donde va el golpeo que estas cargando, si nadie lo toca.
 *
 * No se inventa la trayectoria: saca la salida del balon de `kickLaunch` (la
 * misma que usa `kickBall`) y la vuela con `stepBall` y `collideBallWithWalls`,
 * las mismas del partido. Asi la mira enseña lo que de verdad sale: el golpeo
 * cruzado a la carrera, lo que el balon hereda de la carrera, la curva de
 * colocacion, el efecto que se apaga, la altura y hasta el rebote en la banda.
 *
 * Existe por lo que se reporto en Discord (2026-09-29): "el control del angulo
 * al chutar va algo oxidado". La flecha dibujaba la direccion de la mira, pero
 * el balon salia con el castigo por golpear cruzado a la carrera (hasta ~6° a
 * 90° corriendo) y con curva de colocacion parado, que no se veian: la mira
 * prometia un sitio y el balon iba a otro.
 *
 * Es solo lectura: ni el jugador ni el balon del partido se tocan.
 */
export type KickOutcome = "goal" | "post" | "wide" | "short";

export interface KickPreview {
  /** La trayectoria, como polilinea en coordenadas del campo (x, y, x, y…). */
  path: number[];
  /** Como acaba en la porteria que atacas: dentro, al palo, fuera o no llega. */
  outcome: KickOutcome;
  /** Donde acaba (el punto de la linea de gol si llega). */
  x: number;
  y: number;
}

/** Cuanto se vuela como mucho. Un tiro que en esto no llega, no llega. */
const MAX_SECONDS = 2.6;
/** Por debajo de esta velocidad el balon ya no es un disparo. */
const STOP_SPEED = 40;

export function previewKick(
  player: Player,
  ctx: KickContext,
  ballRadius: number,
  seconds = MAX_SECONDS,
): KickPreview {
  const launch = kickLaunch(player, ctx, ballRadius);
  const ball: Ball = {
    pos: { x: launch.x, y: launch.y },
    prevPos: { x: launch.x, y: launch.y },
    vel: { x: launch.vx, y: launch.vy },
    radius: ballRadius,
    spin: launch.spin,
    topSpin: launch.topSpin,
    loft: 0,
    prevLoft: 0,
    loftVel: launch.loftVel,
    unstoppable: false,
  };
  const path = [ball.pos.x, ball.pos.y];
  const goalX = player.side === "home" ? FIELD.WIDTH : 0;
  const mouth = goalMouth();
  const done = (outcome: KickOutcome): KickPreview => ({ path, outcome, x: ball.pos.x, y: ball.pos.y });

  const steps = Math.ceil(seconds / PHYSICS.DT);
  for (let i = 0; i < steps; i++) {
    stepBall(ball, PHYSICS.DT);
    const { goal, postHit } = collideBallWithWalls(ball);
    path.push(ball.pos.x, ball.pos.y);
    if (goal) return done(goal.scorer === player.side ? "goal" : "wide");
    if (postHit) return done("post");
    // Contra el fondo, fuera de la boca: la pared de la linea de gol.
    const atEndLine = Math.abs(ball.pos.x - goalX) <= ball.radius + 2;
    if (atEndLine && (ball.pos.y < mouth.top || ball.pos.y > mouth.bottom)) return done("wide");
    if (len2(ball.vel.x, ball.vel.y) < STOP_SPEED) return done("short");
  }
  return done("short");
}
